import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Ban,
  BarChart3,
  History,
  Inbox,
  Loader2,
  NotebookText,
  Play,
  RefreshCw,
} from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { formatDateTime } from "@/utils/dateUtils";
import { whatsappApi } from "@/services/whatsapp-api";
import { WhatsAppFilaRevisao } from "@/components/whatsapp/WhatsAppFilaRevisao";
import { WhatsAppHistorico } from "@/components/whatsapp/WhatsAppHistorico";
import { WhatsAppIgnorados } from "@/components/whatsapp/WhatsAppIgnorados";
import { WhatsAppMetricas } from "@/components/whatsapp/WhatsAppMetricas";
import { WhatsAppResumos } from "@/components/whatsapp/WhatsAppResumos";
import { toUserError } from "@/lib/api-error";

export default function WhatsAppAtualizacoesPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const resumoQuery = useQuery({
    queryKey: ["whatsapp-resumo"],
    queryFn: () => whatsappApi.getResumo(),
    refetchInterval: (query) =>
      query.state.data?.ultima_run?.status === "running" ? 3000 : false,
  });

  const pendentesQuery = useQuery({
    queryKey: ["whatsapp-items", "pendente"],
    queryFn: () => whatsappApi.listItems({ status: "pendente", limit: 500 }),
  });

  // O resumo tem polling próprio; a fila não. Sem isto, terminar um
  // processamento deixava o cabeçalho com o número novo e a lista com o
  // conteúdo velho em cache.
  const rodavaAntes = useRef(false);
  const processandoAgora = resumoQuery.data?.ultima_run?.status === "running";
  useEffect(() => {
    if (rodavaAntes.current && !processandoAgora) {
      queryClient.invalidateQueries({ queryKey: ["whatsapp-items"] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-metricas"] });
    }
    rodavaAntes.current = processandoAgora;
  }, [processandoAgora, queryClient]);

  const historicoQuery = useQuery({
    queryKey: ["whatsapp-items", "historico"],
    queryFn: () =>
      whatsappApi.listItems({
        status: "aprovado,rejeitado,redundante",
        limit: 500,
      }),
  });

  const metricasQuery = useQuery({
    queryKey: ["whatsapp-metricas"],
    queryFn: () => whatsappApi.getMetricas(),
  });

  const processar = useMutation({
    mutationFn: () => whatsappApi.processar(),
    onSuccess: () => {
      toast({
        title: "Processamento iniciado",
        description: "As conversas sincronizadas estão sendo analisadas.",
      });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-resumo"] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-items"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Não foi possível iniciar",
        description: toUserError(error).description,
        variant: "destructive",
      });
    },
  });

  const resumo = resumoQuery.data;
  const processando = resumo?.ultima_run?.status === "running";
  const pendentes = pendentesQuery.data?.items ?? [];
  const historico = historicoQuery.data?.items ?? [];

  const decididos = (resumo?.aprovados ?? 0) + (resumo?.descartados ?? 0);
  const totalRevisavel = decididos + (resumo?.pendentes ?? 0);
  const progresso =
    totalRevisavel > 0 ? Math.round((decididos / totalRevisavel) * 100) : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Atualizações do WhatsApp"
        subtitle="Conversas do canal Corisco Frilas viram registros de disponibilidade, negociação e sentimento, revisados aqui antes de valer."
        actions={
          <Button
            onClick={() => processar.mutate()}
            disabled={processar.isPending || processando}
            className="gap-1.5"
          >
            {processar.isPending || processando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {processando ? "Processando..." : "Processar agora"}
          </Button>
        }
      />

      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
          <div className="space-y-1">
            <div className="text-sm">
              {processando ? (
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  Analisando conversas...
                </span>
              ) : resumo?.ultima_run?.finished_at ? (
                <>
                  <span className="font-medium">Última atualização:</span>{" "}
                  {formatDateTime(resumo.ultima_run.finished_at)}
                  {resumo.ultima_run.conversas_processadas != null && (
                    <span className="text-muted-foreground">
                      {" · "}
                      {resumo.ultima_run.conversas_processadas} conversas analisadas
                    </span>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground">
                  Nenhum processamento ainda.
                </span>
              )}
            </div>
            {resumo?.ultima_run?.status === "error" && (
              <div className="flex items-center gap-1.5 text-sm text-red-700">
                <AlertTriangle className="h-3.5 w-3.5" />
                A última rodada falhou.
              </div>
            )}
          </div>

          {totalRevisavel > 0 && (
            <div className="min-w-[260px] flex-1 space-y-1.5 sm:max-w-sm">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">
                  {resumo?.pendentes === 0
                    ? "Tudo revisado"
                    : `${resumo?.pendentes} pra revisar`}
                </span>
                <span className="text-muted-foreground">
                  {decididos} de {totalRevisavel} revisados
                </span>
              </div>
              <Progress value={progresso} className="h-2" />
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="pendencias">
        <TabsList>
          <TabsTrigger value="pendencias" className="gap-1.5">
            <Inbox className="h-4 w-4" />
            Pendências
            {(resumo?.pendentes ?? 0) > 0 && (
              <span className="rounded-full bg-primary/10 px-1.5 text-xs text-primary">
                {resumo?.pendentes}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="resumos" className="gap-1.5">
            <NotebookText className="h-4 w-4" />
            Resumos
          </TabsTrigger>
          <TabsTrigger value="historico" className="gap-1.5">
            <History className="h-4 w-4" />
            Histórico
          </TabsTrigger>
          <TabsTrigger value="metricas" className="gap-1.5">
            <BarChart3 className="h-4 w-4" />
            Métricas
          </TabsTrigger>
          <TabsTrigger value="ignorados" className="gap-1.5">
            <Ban className="h-4 w-4" />
            Contatos ignorados
            {(resumo?.contatos_ignorados ?? 0) > 0 && (
              <span className="rounded-full bg-muted px-1.5 text-xs">
                {resumo?.contatos_ignorados}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pendencias" className="mt-4">
          {pendentesQuery.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <WhatsAppFilaRevisao
              items={pendentes}
              vazio={
                (resumo?.pendentes ?? 0) > 0
                  ? "Atualizando a lista..."
                  : historico.length > 0
                    ? "Nada pendente. Tudo que a IA extraiu já foi revisado."
                    : "Nenhuma extração ainda. Clique em Processar agora."
              }
            />
          )}
        </TabsContent>

        <TabsContent value="resumos" className="mt-4">
          <WhatsAppResumos />
        </TabsContent>

        <TabsContent value="historico" className="mt-4">
          {historicoQuery.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <WhatsAppHistorico items={historico} />
          )}
        </TabsContent>

        <TabsContent value="metricas" className="mt-4">
          {metricasQuery.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <WhatsAppMetricas
              dados={metricasQuery.data ?? { series: [], por_conversa_ultima: [] }}
            />
          )}
        </TabsContent>

        <TabsContent value="ignorados" className="mt-4">
          <WhatsAppIgnorados />
        </TabsContent>
      </Tabs>
    </div>
  );
}
